import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, every, map, mergeMap } from 'rxjs/operators';
import { httpStatus } from '../../../util/http/http-status';
import * as CryptoJS from 'crypto-js';

export interface IPasswordExposureCheckResult {
	passwordChecked: boolean;
	isCompromised?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class CompromisedPasswordsService {
	private httpBackend = inject(HttpBackend);

	public readonly pwnedPasswordsSearchByRangeEndpoint = 'https://api.pwnedpasswords.com/range/';
	private readonly passwordPrefixLength = 5;


	constructor() {}

	checkPasswordExposure(password: string): Observable<IPasswordExposureCheckResult> {
		const { hashPrefix, hashSuffix } = this.hashAndSplitPassword(password);

		return this.getCompromisedPasswordHashesStartingWith(hashPrefix).pipe(
			mergeMap((response) => response.split('\r\n')),
			every((compromisedPassword) => !compromisedPassword.startsWith(hashSuffix)),
			map((isSecure) => (isSecure ? this.secureResponse : this.compromisedResponse)),
			catchError((error) =>
				error.status === httpStatus.NOT_FOUND_404 ? of(this.secureResponse) : of(this.errorResponse)
			)
		);
	}

	private hashAndSplitPassword(password: string): { hashPrefix: string; hashSuffix: string } {
		const passwordHash = CryptoJS.SHA1(password).toString().toUpperCase();

		const hashPrefix = passwordHash.substring(0, this.passwordPrefixLength);
		const hashSuffix = passwordHash.substring(this.passwordPrefixLength);

		return { hashPrefix, hashSuffix };
	}

	private getCompromisedPasswordHashesStartingWith = (hashedChars: string): Observable<string> =>
		new HttpClient(this.httpBackend).get(this.pwnedPasswordsSearchByRangeEndpoint + hashedChars, {
			responseType: 'text',
			withCredentials: false,
		});

	private secureResponse: IPasswordExposureCheckResult = {
		passwordChecked: true,
		isCompromised: false,
	};

	private compromisedResponse: IPasswordExposureCheckResult = {
		passwordChecked: true,
		isCompromised: true,
	};

	private errorResponse: IPasswordExposureCheckResult = {
		passwordChecked: false,
		isCompromised: null,
	};
}
